// Simple memory-based path management for VRP
import { useState, useCallback, useRef } from 'react';
import { 
  isAutoMode, 
  isCustomMode, 
  isSetupMode
} from '../ModeServices/ModeServices';

// Hook for memory-based path management
export const usePureMemoryPathManagement = (locations) => {
  // Path storage for different path modes - pure memory
  const [autoPath, setAutoPath] = useState([]);
  const [customPath, setCustomPath] = useState([]);
  
  // Vehicle count state for VRP
  const [autoVehicleCount, setAutoVehicleCount] = useState(2);
  const [customVehicleCount, setCustomVehicleCount] = useState(2);
  
  // Used to prevent circular updates
  const prevAutoPathRef = useRef(null);
  const prevCustomPathRef = useRef(null);
  
  // Save auto path (memory only - like TSP)
  const saveAutoPath = useCallback((path, vehicleCount = 2, vehiclePaths = null) => {prevAutoPathRef.current = path ? [...path] : null;
    
    // Store vehicle information in memory
    if (vehicleCount) {
      setAutoVehicleCount(vehicleCount);
    }
    
    // Attach vehicle paths metadata if provided
    if (vehiclePaths && Array.isArray(path)) {
      const pathWithMetadata = [...path];
      pathWithMetadata.vehiclePaths = vehiclePaths;
      pathWithMetadata.vehicleCount = vehicleCount;
      setAutoPath(pathWithMetadata);
    } else {
      setAutoPath(path);
    }
  }, []);

  // Save custom path (memory only - like TSP)
  const saveCustomPath = useCallback((path, vehicleCount = 2, vehiclePaths = null) => {prevCustomPathRef.current = path ? [...path] : null;
    
    // Store vehicle information in memory
    if (vehicleCount) {
      setCustomVehicleCount(vehicleCount);
    }
    
    // Attach vehicle paths metadata if provided
    if (vehiclePaths && Array.isArray(path)) {
      const pathWithMetadata = [...path];
      pathWithMetadata.vehiclePaths = vehiclePaths;
      pathWithMetadata.vehicleCount = vehicleCount;
      setCustomPath(pathWithMetadata);
    } else {
      setCustomPath(path);
    }
  }, []);

  // Set path for current mode (simplified like TSP)
  // Support both old and new API for backward compatibility
  const setPath = useCallback((path, currentModeOrMetadata = null, metadata = null) => {
    // Detect if using old API (path, metadata) or new API (path, currentMode, metadata)
    let currentMode = null;
    let actualMetadata = null;
    
    if (typeof currentModeOrMetadata === 'string') {
      // New API: setPath(path, currentMode, metadata)
      currentMode = currentModeOrMetadata;
      actualMetadata = metadata;
    } else {
      // Old API: setPath(path, metadata) - determine mode automatically
      actualMetadata = currentModeOrMetadata;
      // Auto-detect mode based on metadata or path properties
      if (actualMetadata?.vehiclePaths || actualMetadata?.customVehicleCount || path?.vehiclePaths) {
        currentMode = 'Custom Path'; // Custom mode has vehicle paths or custom vehicle count
      } else {
        currentMode = 'Auto Find Path'; // Default to auto mode
      }
    }if (isAutoMode(currentMode)) {
      const vehicleCount = actualMetadata?.vehicleCount || autoVehicleCount;
      const vehiclePaths = actualMetadata?.vehiclePaths || path?.vehiclePaths;
      
      // Attach metadata to path for consistency
      if (vehiclePaths && Array.isArray(path)) {
        const pathWithMetadata = [...path];
        pathWithMetadata.vehiclePaths = vehiclePaths;
        pathWithMetadata.vehicleCount = vehicleCount;
        setAutoPath(pathWithMetadata);
      } else {
        setAutoPath(path);
      }
      setAutoVehicleCount(vehicleCount);
      
      // Update reference for consistency
      if (path && path.length > 1) {
        saveAutoPath(path, vehicleCount, vehiclePaths);
      }
    } else if (isCustomMode(currentMode)) {
      const vehicleCount = actualMetadata?.customVehicleCount || actualMetadata?.vehicleCount || customVehicleCount;
      const vehiclePaths = actualMetadata?.vehiclePaths || path?.vehiclePaths;

      // Attach metadata to path for consistency
      if (Array.isArray(path)) {
        const pathWithMetadata = [...path];
        // Always attach metadata, even if vehiclePaths is null
        pathWithMetadata.vehiclePaths = vehiclePaths;
        pathWithMetadata.customVehicleCount = vehicleCount;
        setCustomPath(pathWithMetadata);
      } else {
        setCustomPath(path);
      }
      setCustomVehicleCount(vehicleCount);
      
      // Update reference for consistency
      if (path && path.length > 1) {
        saveCustomPath(path, vehicleCount, vehiclePaths);
      }
    } else if (isSetupMode(currentMode)) {
      // In setup points mode, clear all paths
      setAutoPath([]);
      setCustomPath([]);
      setAutoVehicleCount(2);
      setCustomVehicleCount(2);
    }
  }, [autoVehicleCount, customVehicleCount, saveAutoPath, saveCustomPath]);

  // Clear all paths (memory only)
  const clearAllPaths = useCallback(() => {setAutoPath([]);
    setCustomPath([]);
    setAutoVehicleCount(2);
    setCustomVehicleCount(2);
    prevAutoPathRef.current = null;
    prevCustomPathRef.current = null;
  }, []);

  // Clear auto path only
  const clearAutoPath = useCallback(() => {setAutoPath([]);
    setAutoVehicleCount(2);
    prevAutoPathRef.current = null;
  }, []);

  // Clear custom path only
  const clearCustomPath = useCallback(() => {setCustomPath([]);
    setCustomVehicleCount(2);
    prevCustomPathRef.current = null;
  }, []);

  return {
    // Path state
    autoPath,
    setAutoPath,
    customPath,
    setCustomPath,
    
    // Vehicle count state
    autoVehicleCount,
    setAutoVehicleCount,
    customVehicleCount,
    setCustomVehicleCount,
    
    // References
    prevAutoPathRef,
    prevCustomPathRef,
    
    // Path operations
    setPath,
    saveAutoPath,
    saveCustomPath,
    clearAllPaths,
    clearAutoPath,
    clearCustomPath
  };
};
